/**
 * DefensePosturePanel - Real-time defense posture display.
 *
 * Shows the current defense posture (GREEN/YELLOW/ORANGE/RED), trigger summary,
 * and scrollable trigger list with severity badges and affected tickers.
 *
 * Receives games as prop from the shell (EyeOfStreamRift).
 */

import { useStreamRiftDefense } from './useStreamRiftDefense';
import type { GameRow } from '@/views/ValueDashboard/types';
import type { DefensePosture, TriggerFiring, TriggerSeverity } from '@/lib/mm-defense/types';

// ── Posture styles ───────────────────────────────────────────────

const POSTURE_STYLES: Record<DefensePosture, { bg: string; text: string; label: string }> = {
  green:  { bg: 'bg-green-500/20',  text: 'text-green-400',  label: 'GREEN' },
  yellow: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'YELLOW' },
  orange: { bg: 'bg-orange-500/20', text: 'text-orange-400', label: 'ORANGE' },
  red:    { bg: 'bg-red-500/20',    text: 'text-red-400',    label: 'RED' },
};

const SEVERITY_STYLES: Record<TriggerSeverity, { bg: string; text: string }> = {
  tier1: { bg: 'bg-yellow-500/20', text: 'text-yellow-400' },
  tier2: { bg: 'bg-orange-500/20', text: 'text-orange-400' },
  tier3: { bg: 'bg-red-500/20',    text: 'text-red-400' },
};

// ── Helpers ──────────────────────────────────────────────────────

function truncateTickers(tickers: string[], max: number = 2): string {
  if (tickers.length === 0) return '';
  const shown = tickers.slice(0, max).map(shortTicker).join(', ');
  const remaining = tickers.length - max;
  return remaining > 0 ? `${shown} +${remaining}` : shown;
}

function shortTicker(ticker: string): string {
  const parts = ticker.split('-');
  return parts[parts.length - 1] || ticker.slice(-6);
}

// ── TriggerRow ───────────────────────────────────────────────────

function TriggerRow({ trigger }: { trigger: TriggerFiring }) {
  const sev = SEVERITY_STYLES[trigger.severity];
  return (
    <div className="hover:bg-muted/30 flex items-center gap-2 px-3 py-1.5">
      {/* Severity badge */}
      <span
        className={`shrink-0 rounded px-1.5 py-px text-[9px] font-bold uppercase ${sev.bg} ${sev.text}`}
      >
        {trigger.severity}
      </span>

      {/* Type name */}
      <span className="text-foreground min-w-0 shrink truncate text-[10px] font-medium">
        {trigger.type.replace(/_/g, ' ')}
      </span>

      {/* Affected tickers */}
      <span className="text-muted-foreground shrink-0 text-[10px] font-mono">
        {truncateTickers(trigger.tickers)}
      </span>

      {/* Value vs threshold */}
      <span className="text-muted-foreground ml-auto shrink-0 text-[10px] tabular-nums">
        {fmtValue(trigger.value)} / {fmtValue(trigger.threshold)}
      </span>
    </div>
  );
}

function fmtValue(v: number): string {
  if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
  if (v >= 100) return Math.round(v).toString();
  if (Number.isInteger(v)) return v.toString();
  return v.toFixed(2);
}

// ── Component ────────────────────────────────────────────────────

interface DefensePosturePanelProps {
  games: GameRow[];
}

export function DefensePosturePanel({ games }: DefensePosturePanelProps) {
  const { status, triggerCount } = useStreamRiftDefense(games);
  const posture = POSTURE_STYLES[status.posture];
  const { tier1, tier2, tier3 } = status.triggerCount;

  // Build summary parts
  const summaryParts: string[] = [];
  if (tier3 > 0) summaryParts.push(`${tier3} tier3`);
  if (tier2 > 0) summaryParts.push(`${tier2} tier2`);
  if (tier1 > 0) summaryParts.push(`${tier1} tier1`);
  const summaryStr = summaryParts.length > 0 ? ` (${summaryParts.join(', ')})` : '';

  return (
    <div className="flex h-full flex-col">
      {/* Posture badge + summary */}
      <div className="shrink-0 px-3 py-2">
        {/* Large posture badge */}
        <div
          className={`flex items-center justify-center rounded py-2 ${posture.bg}`}
        >
          <span className={`text-sm font-black tracking-widest ${posture.text}`}>
            {posture.label}
          </span>
        </div>

        {/* Trigger count summary */}
        <div className="text-muted-foreground mt-1.5 text-center text-[10px]">
          {triggerCount === 0
            ? 'No active triggers'
            : `${triggerCount} trigger${triggerCount !== 1 ? 's' : ''}${summaryStr}`}
        </div>
      </div>

      {/* Scrollable trigger list */}
      <div className="flex-1 overflow-y-auto border-t border-border/50">
        {status.activeTriggers.length === 0 ? (
          <div className="text-muted-foreground/50 flex h-full items-center justify-center text-[10px]">
            All clear
          </div>
        ) : (
          status.activeTriggers
            .slice()
            .sort((a, b) => severityRank(b.severity) - severityRank(a.severity))
            .map((trigger, i) => (
              <TriggerRow key={`${trigger.type}-${i}`} trigger={trigger} />
            ))
        )}
      </div>
    </div>
  );
}

function severityRank(s: TriggerSeverity): number {
  return s === 'tier3' ? 3 : s === 'tier2' ? 2 : 1;
}
